import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LayoutGrid, Package, GraduationCap, Inbox, Users, LogOut, Plus, Trash2, ShieldCheck, Upload, ShoppingCart, Download, Star, FileText, Tag, MessageCircle, CalendarDays, Send, UserPlus } from "lucide-react";
import { Stars } from "@/components/Stars";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { fr } from "date-fns/locale";

function DatePickerFR({ value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const selected = value ? new Date(value + "T00:00:00") : undefined;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="px-3 py-2 text-sm w-full text-left" data-testid={testId}
          style={{ background: "var(--white)", border: "1px solid var(--border)", color: value ? "var(--ink)" : "var(--ink-2)" }}>
          {value ? new Date(value + "T00:00:00").toLocaleDateString("fr-FR") : "Choisir une date"}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" locale={fr} selected={selected}
          onSelect={(d) => {
            if (d) {
              const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
              onChange(iso);
              setOpen(false);
            }
          }} />
      </PopoverContent>
    </Popover>
  );
}

function downloadCSV(filename, rows) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const TABS = [
  { key: "overview", label: "Vue d'ensemble", icon: LayoutGrid },
  { key: "products", label: "Produits", icon: Package },
  { key: "formations", label: "Formations", icon: GraduationCap },
  { key: "sessions", label: "Sessions", icon: CalendarDays },
  { key: "quotes", label: "Devis", icon: Inbox },
  { key: "registrations", label: "Inscriptions", icon: Users },
  { key: "orders", label: "Commandes", icon: ShoppingCart },
  { key: "chatbot", label: "Chatbot", icon: MessageCircle },
  { key: "reviews", label: "Avis", icon: Star },
  { key: "promos", label: "Codes promo", icon: Tag },
  { key: "articles", label: "Conseils", icon: FileText },
  { key: "leads", label: "Prospects", icon: UserPlus },
];

export default function AdminDashboard() {
  const { user, checked, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [formations, setFormations] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reviewsList, setReviewsList] = useState([]);
  const [formationReviews, setFormationReviews] = useState([]);
  const [promos, setPromos] = useState([]);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    if (checked && !user) nav("/admin/login");
    else if (checked && user && user.role !== "admin") nav("/espace-client");
  }, [checked, user, nav]);

  const loadAll = useCallback(async () => {
    try {
      const [s, p, f, q, r] = await Promise.all([
        api.get("/stats"), api.get("/products"), api.get("/formations"), api.get("/quotes"), api.get("/registrations"),
      ]);
      setStats(s.data); setProducts(p.data); setFormations(f.data); setQuotes(q.data); setRegistrations(r.data);
      try { setOrders((await api.get("/orders")).data); } catch { /* noop */ }
      try { setReviewsList((await api.get("/reviews")).data); } catch { /* noop */ }
      try { setFormationReviews((await api.get("/formation-reviews")).data); } catch { /* noop */ }
      try { setPromos((await api.get("/promos")).data); } catch { /* noop */ }
      try { setArticles((await api.get("/articles")).data); } catch { /* noop */ }
    } catch (e) { /* handled per action */ }
  }, []);

  useEffect(() => { if (user) loadAll(); }, [user, loadAll]);

  if (!checked || !user) return <div className="min-h-screen" style={{ background: "var(--ink)" }} />;

  const doLogout = async () => { await logout(); nav("/admin/login"); };

  return (
    <div className="min-h-screen flex flex-col md:flex-row" style={{ background: "var(--bone)" }} data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="md:w-64 shrink-0 flex md:flex-col md:min-h-screen" style={{ background: "var(--ink)", color: "var(--bone)" }}>
        <div className="p-6 hidden md:flex items-center gap-2" style={{ borderBottom: "1px solid rgba(245,245,240,0.15)" }}>
          <ShieldCheck size={24} style={{ color: "var(--orange)" }} />
          <span className="font-display font-extrabold text-lg">ALDOL-SOLUTIONS.</span>
        </div>
        <nav className="flex md:flex-col flex-1 overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            const on = tab === t.key;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
                className="flex items-center gap-3 px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors duration-200"
                style={{ background: on ? "var(--orange)" : "transparent", color: on ? "var(--bone)" : "rgba(245,245,240,0.75)" }}>
                <Icon size={18} /> {t.label}
              </button>
            );
          })}
        </nav>
        <button onClick={doLogout} data-testid="logout-btn" className="flex items-center gap-3 px-6 py-4 text-sm font-medium" style={{ borderTop: "1px solid rgba(245,245,240,0.15)", color: "rgba(245,245,240,0.75)" }}>
          <LogOut size={18} /> Déconnexion
        </button>
      </aside>

      {/* Content */}
      <main className="flex-1 p-6 md:p-12 overflow-x-hidden">
        {tab === "overview" && <Overview stats={stats} />}
        {tab === "products" && <Products products={products} reload={loadAll} />}
        {tab === "formations" && <Formations formations={formations} reload={loadAll} />}
        {tab === "sessions" && <SessionsAdmin formations={formations} />}
        {tab === "quotes" && <Quotes quotes={quotes} reload={loadAll} />}
        {tab === "registrations" && <Registrations registrations={registrations} />}
        {tab === "orders" && <Orders orders={orders} />}
        {tab === "chatbot" && <ChatHistory />}
        {tab === "reviews" && <ReviewsMod reviews={reviewsList} products={products} formationReviews={formationReviews} formations={formations} reload={loadAll} />}
        {tab === "promos" && <Promos promos={promos} reload={loadAll} />}
        {tab === "articles" && <Articles articles={articles} reload={loadAll} />}
        {tab === "leads" && <LeadsAdmin />}
      </main>
    </div>
  );
}

const inputCls = "px-3 py-2 text-sm w-full";
const inputStyle = { background: "var(--white)", border: "1px solid var(--border)", outline: "none" };

function LeadsAdmin() {
  const [leads, setLeads] = useState([]);
  useEffect(() => { api.get("/leads").then((r) => setLeads(r.data)).catch(() => {}); }, []);
  const exportCSV = () => downloadCSV("prospects-livre-blanc.csv", leads.map((l) => ({
    nom: l.name, email: l.email, entreprise: l.company || "", date: (l.created_at || "").slice(0, 10),
    relance_envoyee: l.followup_sent ? "oui" : "non",
  })));
  return (
    <div data-testid="leads-admin">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display font-black text-3xl">Prospects livre blanc</h1>
          <p className="text-sm mt-1" style={{ color: "var(--ink-2)" }}>{leads.length} prospect(s) — relance automatique J+3 avec diagnostic offert</p>
        </div>
        <button onClick={exportCSV} disabled={!leads.length} className="btn-primary px-5 py-3 text-sm font-semibold inline-flex items-center gap-2" data-testid="leads-export-btn">
          <Download size={16} /> Export CSV
        </button>
      </div>
      <div className="overflow-x-auto" style={{ background: "var(--white)", border: "1px solid var(--border)" }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: "1px solid var(--border)" }}>
              {["Nom", "Email", "Entreprise", "Date", "Relance J+3"].map((h) => (
                <th key={h} className="text-left px-5 py-4 font-display font-bold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {leads.map((l, i) => (
              <tr key={l.id} data-testid={`lead-row-${i}`} style={{ borderBottom: "1px solid var(--border)" }}>
                <td className="px-5 py-4 font-semibold">{l.name}</td>
                <td className="px-5 py-4">{l.email}</td>
                <td className="px-5 py-4" style={{ color: "var(--ink-2)" }}>{l.company || "—"}</td>
                <td className="px-5 py-4" style={{ color: "var(--ink-2)" }}>{(l.created_at || "").slice(0, 10)}</td>
                <td className="px-5 py-4">
                  <span className="text-xs font-bold uppercase px-2 py-1" style={l.followup_sent
                    ? { background: "rgba(62,142,90,0.12)", color: "#3E8E5A" }
                    : { background: "rgba(232,93,4,0.1)", color: "var(--orange)" }}>
                    {l.followup_sent ? "Envoyée" : "En attente"}
                  </span>
                </td>
              </tr>
            ))}
            {!leads.length && <tr><td colSpan={5} className="px-5 py-10 text-center" style={{ color: "var(--ink-2)" }}>Aucun prospect pour le moment.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SessionsAdmin({ formations }) {
  const [sessions, setSessions] = useState([]);
  const [regs, setRegs] = useState([]);
  const [form, setForm] = useState({ formation_id: "", date: "", city: "Saint-Maur-des-Fossés (94)", seats_left: 8 });
  const [edit, setEdit] = useState(null);
  const [nlLoading, setNlLoading] = useState(false);

  const load = () => {
    api.get("/sessions").then((r) => setSessions(r.data)).catch(() => {});
    api.get("/session-registrations").then((r) => setRegs(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.date) { toast.error("Choisissez une date"); return; }
    try {
      await api.post("/sessions", { ...form, seats_left: Number(form.seats_left) });
      toast.success("Session ajoutée");
      setForm({ ...form, date: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const save = async () => {
    try {
      await api.put(`/sessions/${edit.id}`, { formation_id: edit.formation_id, date: edit.date, city: edit.city, seats_left: Number(edit.seats_left) });
      toast.success("Session mise à jour");
      setEdit(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/sessions/${id}`);
      toast.success("Session supprimée");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const sendNewsletter = async () => {
    setNlLoading(true);
    try {
      const { data } = await api.post("/newsletter/send-sessions");
      if (data.sent) toast.success(`Newsletter envoyée à ${data.sent} abonné(s)`);
      else toast.info(`Aucun envoi : ${data.subscribers} abonné(s), ${data.sessions} session(s) à venir`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setNlLoading(false); }
  };

  return (
    <div data-testid="sessions-admin">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h1 className="font-display font-black text-3xl">Sessions inter-entreprises</h1>
        <button onClick={sendNewsletter} disabled={nlLoading} className="btn-primary px-5 py-2.5 text-sm font-bold inline-flex items-center gap-2" data-testid="send-sessions-newsletter-btn">
          <Send size={15} /> {nlLoading ? "Envoi..." : "Envoyer la newsletter sessions"}
        </button>
      </div>

      <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-5 gap-3 p-5 mb-8" style={{ background: "var(--white)", border: "1px solid var(--border)" }} data-testid="add-session-form">
        <select required value={form.formation_id} onChange={(e) => setForm({ ...form, formation_id: e.target.value })} className={`${inputCls} md:col-span-2`} style={inputStyle} data-testid="session-formation-select">
          <option value="">— Choisir une formation —</option>
          {formations.map((f) => <option key={f.id} value={f.id}>{f.title}</option>)}
        </select>
        <DatePickerFR value={form.date} onChange={(d) => setForm({ ...form, date: d })} testId="session-date-input" />
        <input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Lieu" className={inputCls} style={inputStyle} data-testid="session-city-input" />
        <div className="flex gap-2">
          <input required type="number" min="0" max="50" value={form.seats_left} onChange={(e) => setForm({ ...form, seats_left: e.target.value })} placeholder="Places" className={inputCls} style={inputStyle} data-testid="session-seats-input" />
          <button type="submit" className="btn-primary px-4 shrink-0" data-testid="add-session-btn"><Plus size={17} /></button>
        </div>
      </form>

      <div className="overflow-x-auto" style={{ border: "1px solid var(--border)" }}>
        <table className="w-full text-sm" style={{ background: "var(--white)" }}>
          <thead>
            <tr className="text-left" style={{ background: "var(--ink)", color: "var(--bone)" }}>
              <th className="px-4 py-3 font-semibold">Date</th><th className="px-4 py-3 font-semibold">Formation</th>
              <th className="px-4 py-3 font-semibold">Lieu</th><th className="px-4 py-3 font-semibold">Places</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((s) => (
              <tr key={s.id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`admin-session-${s.id}`}>
                {edit?.id === s.id ? (
                  <>
                    <td className="px-4 py-2"><DatePickerFR value={edit.date} onChange={(d) => setEdit({ ...edit, date: d })} testId={`edit-date-${s.id}`} /></td>
                    <td className="px-4 py-2 font-semibold">{s.formation_title}</td>
                    <td className="px-4 py-2"><input value={edit.city} onChange={(e) => setEdit({ ...edit, city: e.target.value })} className={inputCls} style={inputStyle} /></td>
                    <td className="px-4 py-2"><input type="number" min="0" max="50" value={edit.seats_left} onChange={(e) => setEdit({ ...edit, seats_left: e.target.value })} className={inputCls} style={inputStyle} /></td>
                    <td className="px-4 py-2 whitespace-nowrap text-right">
                      <button onClick={save} className="btn-primary px-3 py-1.5 text-xs font-bold mr-2" data-testid={`save-session-${s.id}`}>Enregistrer</button>
                      <button onClick={() => setEdit(null)} className="text-xs underline">Annuler</button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-3 font-bold whitespace-nowrap" style={{ color: "var(--orange)" }}>{new Date(s.date + "T00:00:00").toLocaleDateString("fr-FR")}</td>
                    <td className="px-4 py-3">{s.formation_title}<span className="block text-xs" style={{ color: "var(--ink-2)" }}>{s.domain}</span></td>
                    <td className="px-4 py-3">{s.city}</td>
                    <td className="px-4 py-3">{s.seats_left}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <button onClick={() => setEdit({ ...s })} className="text-xs font-bold underline mr-3" data-testid={`edit-session-${s.id}`}>Modifier</button>
                      <button onClick={() => remove(s.id)} style={{ color: "var(--orange)" }} data-testid={`delete-session-${s.id}`}><Trash2 size={15} /></button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {sessions.length === 0 && <p className="p-6 text-center text-sm" style={{ color: "var(--ink-2)" }}>Aucune session à venir.</p>}
      </div>

      <h2 className="font-display font-black text-2xl mt-12 mb-4">Inscriptions aux sessions ({regs.length})</h2>
      <div className="overflow-x-auto" style={{ border: "1px solid var(--border)" }}>
        <table className="w-full text-sm" style={{ background: "var(--white)" }}>
          <thead>
            <tr className="text-left" style={{ background: "var(--ink)", color: "var(--bone)" }}>
              <th className="px-4 py-3 font-semibold">Reçue le</th><th className="px-4 py-3 font-semibold">Contact</th>
              <th className="px-4 py-3 font-semibold">Session</th><th className="px-4 py-3 font-semibold">Participants</th><th className="px-4 py-3 font-semibold">Rappel J-7</th>
            </tr>
          </thead>
          <tbody>
            {regs.map((r) => (
              <tr key={r.id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`admin-sessreg-${r.id}`}>
                <td className="px-4 py-3 whitespace-nowrap">{new Date(r.created_at).toLocaleDateString("fr-FR")}</td>
                <td className="px-4 py-3">{r.name}{r.company ? ` (${r.company})` : ""}<span className="block text-xs" style={{ color: "var(--ink-2)" }}>{r.email} · {r.phone || "-"}</span></td>
                <td className="px-4 py-3">{r.formation_title}<span className="block text-xs" style={{ color: "var(--ink-2)" }}>{new Date(r.session_date + "T00:00:00").toLocaleDateString("fr-FR")} · {r.city}</span></td>
                <td className="px-4 py-3 text-center font-bold">{r.participants}</td>
                <td className="px-4 py-3">{r.reminder_sent ? "✔ Envoyé" : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {regs.length === 0 && <p className="p-6 text-center text-sm" style={{ color: "var(--ink-2)" }}>Aucune inscription pour le moment.</p>}
      </div>
    </div>
  );
}

function Overview({ stats }) {
  const [monthly, setMonthly] = useState([]);
  useEffect(() => {
    api.get("/stats/monthly").then((r) => setMonthly(r.data)).catch(() => {});
  }, []);
  const maxSales = Math.max(...monthly.map((m) => m.sales), 1);
  const monthLabel = (mo) => {
    const names = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
    return `${names[parseInt(mo.slice(5), 10) - 1]} ${mo.slice(2, 4)}`;
  };
  const cards = [
    ["Produits", stats?.products], ["Formations", stats?.formations],
    ["Devis reçus", stats?.quotes], ["Nouveaux devis", stats?.quotes_new], ["Devis acceptés", stats?.quotes_accepted], ["Taux de conversion", stats?.quotes_conversion != null ? `${stats.quotes_conversion} %` : "—"], ["Inscriptions", stats?.registrations],
    ["Commandes payées", stats?.orders_paid], ["Abonnés newsletter", stats?.subscribers],
  ];
  return (
    <div data-testid="overview-panel">
      <h1 className="font-display font-black text-4xl mb-8">Vue d'ensemble</h1>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {cards.map(([l, v]) => (
          <div key={l} className="p-6" style={{ background: "var(--white)", border: "1px solid var(--border)" }}>
            <p className="font-display font-black text-4xl" style={{ color: "var(--orange)" }}>{v ?? "—"}</p>
            <p className="overline mt-2" style={{ color: "var(--ink-2)" }}>{l}</p>
          </div>
        ))}
      </div>

      {monthly.length > 0 && (
        <div className="mt-10 p-8" style={{ background: "var(--white)", border: "1px solid var(--border)" }} data-testid="sales-chart">
          <h2 className="font-display font-bold text-2xl mb-8">Ventes & devis — 6 derniers mois</h2>
          <div className="grid grid-cols-6 gap-4 items-end pb-0" style={{ height: "220px", borderBottom: "2px solid var(--border)" }}>
            {monthly.map((m) => (
              <div key={m.month} className="flex flex-col items-center justify-end h-full gap-2" data-testid={`chart-month-${m.month}`}>
                <span className="text-xs font-semibold whitespace-nowrap">{`${m.sales.toFixed(0)} €`}</span>
                <div className="w-full max-w-[56px] transition-all duration-500" title={`${m.sales.toFixed(2)} € — ${m.orders} commande(s)`}
                  style={{ height: `${Math.max((m.sales / maxSales) * 100, 2)}%`, background: m.sales > 0 ? "var(--orange)" : "var(--border)" }} />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-6 gap-4 mt-2">
            {monthly.map((m) => (
              <div key={m.month} className="flex flex-col items-center">
                <span className="text-xs" style={{ color: "var(--ink-2)" }}>{monthLabel(m.month)}</span>
                <span className="text-[10px] font-mono2" style={{ color: "var(--ink-2)" }}>{m.orders} cmd · {m.quotes} devis</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ImageField({ value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image téléversée");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <div className="md:col-span-2">
      <div className="flex flex-col sm:flex-row gap-3">
        <input className="field flex-1" placeholder="URL image *" required value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
        <label className="btn-outline px-5 py-3 font-semibold flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap" data-testid={`${testid}-upload`}>
          <Upload size={16} /> {busy ? "Envoi..." : "Téléverser"}
          <input type="file" accept="image/*" className="hidden" onChange={handle} />
        </label>
      </div>
      {value && <img src={value} alt="" className="mt-3 w-24 h-24 object-cover" style={{ border: "1px solid var(--border)" }} />}
    </div>
  );
}

function Products({ products, reload }) {
  const empty = { name: "", category: "ergonomie", subcategory: "", price: "", short: "", description: "", image: "", badge: "", stock: "disponible", stock_qty: "" };
  const [form, setForm] = useState(empty);
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/products", { ...form, price: parseFloat(form.price), stock_qty: form.stock_qty === "" ? null : parseInt(form.stock_qty, 10) });
      toast.success("Produit ajouté"); setForm(empty); reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/products/${id}`); toast.success("Produit supprimé"); reload(); };
  return (
    <div data-testid="products-panel">
      <h1 className="font-display font-black text-4xl mb-8">Produits</h1>
      <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-10 p-6" style={{ background: "var(--white)", border: "1px solid var(--border)" }} data-testid="product-form">
        <input className="field" placeholder="Nom *" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pf-name" />
        <select className="field" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="pf-category">
          <option value="ergonomie">Ergonomie</option>
          <option value="epi">EPI</option>
        </select>
        <input className="field" placeholder="Sous-catégorie (ex: Souris ergonomiques)" value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} data-testid="pf-subcategory" />
        <input className="field" type="number" step="0.01" placeholder="Prix € *" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="pf-price" />
        <input className="field" placeholder="Badge (optionnel)" value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} data-testid="pf-badge" />
        <select className="field" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="pf-stock">
          <option value="disponible">En stock</option>
          <option value="sur_commande">Sur commande</option>
        </select>
        <input className="field" type="number" min="0" placeholder="Quantité en stock (vide = non suivi)" value={form.stock_qty} onChange={(e) => setForm({ ...form, stock_qty: e.target.value })} data-testid="pf-stock-qty" />
        <input className="field md:col-span-2" placeholder="Description courte *" required value={form.short} onChange={(e) => setForm({ ...form, short: e.target.value })} data-testid="pf-short" />
        <ImageField value={form.image} onChange={(v) => setForm({ ...form, image: v })} testid="pf-image" />
        <button className="btn-primary py-3 px-6 font-semibold md:col-span-2 flex items-center justify-center gap-2" data-testid="pf-submit"><Plus size={18} /> Ajouter le produit</button>
      </form>
      <div className="space-y-px" style={{ background: "var(--border)", border: "1px solid var(--border)" }}>
        {products.map((p) => (
          <div key={p.id} className="flex items-center gap-4 p-4" style={{ background: "var(--white)" }} data-testid={`admin-product-${p.id}`}>
            <img src={p.image} alt="" className="w-14 h-14 object-cover" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{p.name}</p>
              <p className="text-sm" style={{ color: "var(--ink-2)" }}>{p.category === "epi" ? "EPI" : "Ergonomie"}{p.subcategory ? ` · ${p.subcategory}` : ""} · {p.price > 0 ? `${p.price.toFixed(2)} €` : "Sur devis"}{p.stock_qty != null ? ` · ${p.stock_qty} en stock` : p.stock === "sur_commande" ? " · Sur commande" : ""}</p>
            </div>
            <button onClick={() => del(p.id)} className="p-2" style={{ color: "var(--orange-hover)" }} data-testid={`del-product-${p.id}`}><Trash2 size={18} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Formations({ formations, reload }) {
  const empty = { title: "", duration: "", level: "", price: "", description: "", image: "" };
  const [form, setForm] = useState(empty);
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/formations", { ...form, price: parseFloat(form.price) });
      toast.success("Formation ajoutée"); setForm(empty); reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/formations/${id}`); toast.success("Formation supprimée"); reload(); };
  return (
    <div data-testid="formations-panel">
      <h1 className="font-display font-black text-4xl mb-8">Formations</h1>
      <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-10 p-6" style={{ background: "var(--white)", border: "1px solid var(--border)" }} data-testid="formation-form">
        <input className="field md:col-span-2" placeholder="Titre *" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ff-title" />
        <input className="field" placeholder="Durée (ex: 2 jours) *" required value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} data-testid="ff-duration" />
        <input className="field" placeholder="Niveau *" required value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} data-testid="ff-level" />
        <input className="field" type="number" step="0.01" placeholder="Prix € *" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="ff-price" />
        <ImageField value={form.image} onChange={(v) => setForm({ ...form, image: v })} testid="ff-image" />
        <textarea className="field md:col-span-2" placeholder="Description *" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="ff-description" />
        <button className="btn-primary py-3 px-6 font-semibold md:col-span-2 flex items-center justify-center gap-2" data-testid="ff-submit"><Plus size={18} /> Ajouter la formation</button>
      </form>
      <div className="space-y-px" style={{ background: "var(--border)", border: "1px solid var(--border)" }}>
        {formations.map((f) => (
          <div key={f.id} className="flex items-center gap-4 p-4" style={{ background: "var(--white)" }} data-testid={`admin-formation-${f.id}`}>
            <img src={f.image} alt="" className="w-14 h-14 object-cover" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{f.title}</p>
              <p className="text-sm" style={{ color: "var(--ink-2)" }}>{f.duration} · {f.level} · {f.price.toFixed(0)} €</p>
            </div>
            <button onClick={() => del(f.id)} className="p-2" style={{ color: "var(--orange-hover)" }} data-testid={`del-formation-${f.id}`}><Trash2 size={18} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Quotes({ quotes, reload }) {
  const setStatus = async (id, status) => { await api.patch(`/quotes/${id}?status=${status}`); reload(); };
  const accepted = quotes.filter((q) => q.status === "accepté").length;
  return (
    <div data-testid="quotes-panel">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display font-black text-4xl">Demandes de devis</h1>
          {quotes.length > 0 && (
            <p className="text-sm mt-1" style={{ color: "var(--ink-2)" }} data-testid="quotes-conversion">
              Taux de conversion : <strong style={{ color: "var(--orange)" }}>{Math.round((accepted / quotes.length) * 100)} %</strong> ({accepted} accepté{accepted > 1 ? "s" : ""} / {quotes.length})
            </p>
          )}
        </div>
        <button onClick={() => downloadCSV("devis.csv", quotes.map((q) => ({ date: (q.created_at || "").slice(0, 10), objet: q.subject, nom: q.name, entreprise: q.company, email: q.email, telephone: q.phone, statut: q.status, message: q.message })))}
          className="btn-outline px-5 py-2.5 text-sm font-semibold flex items-center gap-2" data-testid="export-quotes-btn">
          <Download size={16} /> Exporter CSV
        </button>
      </div>
      <div className="space-y-4">
        {quotes.length === 0 && <p style={{ color: "var(--ink-2)" }}>Aucune demande pour le moment.</p>}
        {quotes.map((q) => (
          <div key={q.id} className="p-6" style={{ background: "var(--white)", border: "1px solid var(--border)" }} data-testid={`quote-${q.id}`}>
            <div className="flex flex-wrap justify-between gap-3 mb-3">
              <div>
                <p className="font-display font-bold text-lg">
                  {q.subject.startsWith("Location") && (
                    <span className="overline px-2 py-0.5 mr-2 align-middle" style={{ background: "var(--orange)", color: "var(--bone)" }} data-testid={`quote-location-tag-${q.id}`}>Location</span>
                  )}
                  {q.subject}
                </p>
                <p className="text-sm" style={{ color: "var(--ink-2)" }}>{q.name}{q.company ? ` · ${q.company}` : ""} · {q.email}{q.phone ? ` · ${q.phone}` : ""}</p>
              </div>
              <span className="overline px-3 py-1 h-fit" style={{ background: q.status === "nouveau" ? "var(--orange)" : "var(--ink)", color: "var(--bone)" }}>{q.status}</span>
            </div>
            <p className="mb-4" style={{ color: "var(--ink-2)" }}>{q.message}</p>
            {q.proposal_sent_at && (
              <p className="mb-3 text-xs font-semibold flex items-center gap-2" style={{ color: "#1E8A3C" }} data-testid={`quote-proposal-info-${q.id}`}>
                ✔ Devis PDF envoyé le {new Date(q.proposal_sent_at).toLocaleDateString("fr-FR")} ({q.proposal_amount?.toFixed(2)} € HT)
                {q.followup_sent && <span style={{ color: "var(--orange)" }}>· Relance J+7 envoyée</span>}
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/quotes/${q.id}/proposal`} target="_blank" rel="noopener noreferrer" className="underline" style={{ color: "var(--ink)" }}>Voir le PDF</a>
              </p>
            )}
            <ProposalForm quote={q} reload={reload} />
            <div className="flex gap-2 mt-4">
              {["nouveau", "traité", "accepté", "archivé"].map((s) => (
                <button key={s} onClick={() => setStatus(q.id, s)} data-testid={`quote-status-${q.id}-${s}`}
                  className="btn-outline px-4 py-2 text-xs font-semibold" style={q.status === s ? { background: "var(--ink)", color: "var(--bone)" } : {}}>{s}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProposalForm({ quote, reload }) {
  const [amount, setAmount] = useState(quote.proposal_amount || "");
  const [details, setDetails] = useState("");
  const [sending, setSending] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post(`/quotes/${quote.id}/send-proposal`, { amount_ht: Number(amount), details });
      toast.success(data.message);
      reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };
  return (
    <form onSubmit={send} className="flex flex-wrap items-center gap-2 p-3" style={{ background: "var(--bone)", border: "1px dashed var(--border)" }} data-testid={`proposal-form-${quote.id}`}>
      <input type="number" step="0.01" min="0.01" required placeholder="Montant HT (€)" value={amount} onChange={(e) => setAmount(e.target.value)}
        className="px-3 py-2 text-sm w-36" style={{ background: "var(--white)", border: "1px solid var(--border)", outline: "none" }} data-testid={`proposal-amount-${quote.id}`} />
      <input placeholder="Détails de la prestation (optionnel)" value={details} onChange={(e) => setDetails(e.target.value)}
        className="px-3 py-2 text-sm flex-1 min-w-[200px]" style={{ background: "var(--white)", border: "1px solid var(--border)", outline: "none" }} data-testid={`proposal-details-${quote.id}`} />
      <button type="submit" disabled={sending} className="btn-primary px-4 py-2 text-xs font-bold whitespace-nowrap" data-testid={`proposal-send-${quote.id}`}>
        {sending ? "Envoi..." : quote.proposal_sent_at ? "Renvoyer le devis PDF" : "Générer & envoyer le devis PDF"}
      </button>
    </form>
  );
}

function Registrations({ registrations }) {
  return (
    <div data-testid="registrations-panel">
      <h1 className="font-display font-black text-4xl mb-8">Inscriptions formations</h1>
      <div className="space-y-px" style={{ background: "var(--border)", border: "1px solid var(--border)" }}>
        {registrations.length === 0 && <p className="p-6" style={{ background: "var(--white)", color: "var(--ink-2)" }}>Aucune inscription.</p>}
        {registrations.map((r) => (
          <div key={r.id} className="p-5" style={{ background: "var(--white)" }} data-testid={`registration-${r.id}`}>
            <p className="font-semibold">{r.formation_title}</p>
            <p className="text-sm" style={{ color: "var(--ink-2)" }}>{r.name} · {r.email}{r.company ? ` · ${r.company}` : ""}{r.phone ? ` · ${r.phone}` : ""}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChatHistory() {
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);
  const [msgs, setMsgs] = useState([]);

  useEffect(() => {
    api.get("/chat/sessions").then((r) => setSessions(r.data)).catch(() => {});
    api.get("/chat/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const openSession = async (sid) => {
    if (selected === sid) { setSelected(null); return; }
    setSelected(sid);
    try { setMsgs((await api.get(`/chat/sessions/${sid}`)).data); } catch { setMsgs([]); }
  };

  return (
    <div data-testid="chatbot-panel">
      <h1 className="font-display font-black text-4xl mb-2">Conversations du chatbot</h1>
      <p className="mb-6" style={{ color: "var(--ink-2)" }}>{sessions.length} session(s) — découvrez ce que cherchent vos visiteurs.</p>
      {stats?.top_keywords?.length > 0 && (
        <div className="mb-8 p-6" style={{ background: "var(--white)", border: "1px solid var(--border)" }} data-testid="chat-stats">
          <p className="overline mb-3" style={{ color: "var(--orange)" }}>Sujets les plus demandés ({stats.total_questions} questions)</p>
          <div className="flex flex-wrap gap-2">
            {stats.top_keywords.map((k) => (
              <span key={k.word} className="px-3 py-1.5 text-sm font-semibold" style={{ background: "var(--bone)", border: "1px solid var(--border)" }} data-testid={`chat-kw-${k.word}`}>
                {k.word} <span style={{ color: "var(--orange)" }}>×{k.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="space-y-px" style={{ background: "var(--border)", border: "1px solid var(--border)" }}>
        {sessions.length === 0 && <p className="p-6" style={{ background: "var(--white)", color: "var(--ink-2)" }}>Aucune conversation pour le moment.</p>}
        {sessions.map((s) => (
          <div key={s.session_id} style={{ background: "var(--white)" }}>
            <button onClick={() => openSession(s.session_id)} className="w-full flex items-center gap-4 p-5 text-left" data-testid={`chat-session-${s.session_id}`}>
              <MessageCircle size={18} style={{ color: "var(--orange)" }} className="shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{s.preview || "(sans message)"}</p>
                <p className="text-xs font-mono2" style={{ color: "var(--ink-2)" }}>{(s.last_at || "").slice(0, 16).replace("T", " ")} · {s.count} message(s)</p>
              </div>
            </button>
            {selected === s.session_id && (
              <div className="px-5 pb-5 space-y-2" data-testid={`chat-session-messages-${s.session_id}`}>
                {msgs.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className="max-w-[75%] px-3 py-2 text-sm whitespace-pre-wrap"
                      style={m.role === "user"
                        ? { background: "var(--orange)", color: "var(--bone)" }
                        : { background: "var(--bone)", border: "1px solid var(--border)" }}>
                      {m.content}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Orders({ orders }) {
  const paid = orders.filter((o) => o.payment_status === "paid");
  const revenue = paid.reduce((s, o) => s + (o.amount || 0), 0);
  return (
    <div data-testid="orders-panel">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
        <h1 className="font-display font-black text-4xl">Commandes</h1>
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/export`} target="_blank" rel="noopener noreferrer"
          className="btn-outline px-5 py-2.5 text-sm font-semibold flex items-center gap-2" data-testid="export-orders-btn">
          <Download size={16} /> Export comptable CSV (HT / TVA / TTC)
        </a>
      </div>
      <p className="mb-8" style={{ color: "var(--ink-2)" }}>{paid.length} payée(s) · Chiffre d'affaires : <strong>{revenue.toFixed(2)} €</strong></p>
      <div className="space-y-px" style={{ background: "var(--border)", border: "1px solid var(--border)" }}>
        {orders.length === 0 && <p className="p-6" style={{ background: "var(--white)", color: "var(--ink-2)" }}>Aucune commande pour le moment.</p>}
        {orders.map((o) => (
          <div key={o.session_id} className="flex flex-wrap items-center gap-4 p-5" style={{ background: "var(--white)" }} data-testid={`order-${o.session_id}`}>
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{o.product_name || "Commande"}</p>
              <p className="text-sm font-mono2" style={{ color: "var(--ink-2)" }}>{(o.created_at || "").slice(0, 10)} · {o.session_id.slice(0, 22)}…{o.customer_email ? ` · ${o.customer_email}` : ""}{o.promo_code ? ` · ${o.promo_code} (−${o.discount_percent}%)` : ""}</p>
            </div>
            {o.payment_status === "paid" && (
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${o.session_id}/invoice`} target="_blank" rel="noopener noreferrer"
                className="text-xs font-bold link-hover" style={{ color: "var(--orange)" }} data-testid={`invoice-link-${o.session_id}`}>
                Facture PDF
              </a>
            )}
            <span className="font-display font-black text-lg">{(o.amount || 0).toFixed(2)} €</span>
            <span className="overline px-3 py-1" style={{ background: o.payment_status === "paid" ? "var(--orange)" : "var(--ink)", color: "var(--bone)" }}>{o.payment_status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReviewsMod({ reviews, products, formationReviews, formations, reload }) {
  const nameOf = (pid) => (products.find((p) => p.id === pid) || {}).name || "Produit supprimé";
  const fNameOf = (fid) => (formations.find((f) => f.id === fid) || {}).title || "Formation supprimée";
  const del = async (id) => { await api.delete(`/reviews/${id}`); toast.success("Avis supprimé"); reload(); };
  const delF = async (id) => { await api.delete(`/formation-reviews/${id}`); toast.success("Avis supprimé"); reload(); };
  return (
    <div data-testid="reviews-panel">
      <h1 className="font-display font-black text-4xl mb-8">Avis clients</h1>
      <h2 className="font-display font-bold text-2xl mb-4">Avis produits ({reviews.length})</h2>
      <div className="space-y-px mb-12" style={{ background: "var(--border)", border: "1px solid var(--border)" }}>
        {reviews.length === 0 && <p className="p-6" style={{ background: "var(--white)", color: "var(--ink-2)" }}>Aucun avis pour le moment.</p>}
        {reviews.map((r) => (
          <div key={r.id} className="flex items-start gap-4 p-5" style={{ background: "var(--white)" }} data-testid={`admin-review-${r.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <span className="font-semibold">{r.name}</span>
                <Stars value={r.rating} size={14} />
              </div>
              <p className="text-sm mb-1" style={{ color: "var(--ink-2)" }}>{r.comment}</p>
              <p className="text-xs font-mono2" style={{ color: "var(--ink-2)" }}>{nameOf(r.product_id)} · {(r.created_at || "").slice(0, 10)}</p>
            </div>
            <button onClick={() => del(r.id)} className="p-2" style={{ color: "var(--orange-hover)" }} data-testid={`del-review-${r.id}`}><Trash2 size={18} /></button>
          </div>
        ))}
      </div>
      <h2 className="font-display font-bold text-2xl mb-4">Avis formations ({formationReviews.length})</h2>
      <div className="space-y-px" style={{ background: "var(--border)", border: "1px solid var(--border)" }}>
        {formationReviews.length === 0 && <p className="p-6" style={{ background: "var(--white)", color: "var(--ink-2)" }}>Aucun avis pour le moment.</p>}
        {formationReviews.map((r) => (
          <div key={r.id} className="flex items-start gap-4 p-5" style={{ background: "var(--white)" }} data-testid={`admin-formation-review-${r.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <span className="font-semibold">{r.name}</span>
                <Stars value={r.rating} size={14} />
              </div>
              <p className="text-sm mb-1" style={{ color: "var(--ink-2)" }}>{r.comment}</p>
              <p className="text-xs font-mono2" style={{ color: "var(--ink-2)" }}>{fNameOf(r.formation_id)} · {(r.created_at || "").slice(0, 10)}</p>
            </div>
            <button onClick={() => delF(r.id)} className="p-2" style={{ color: "var(--orange-hover)" }} data-testid={`del-formation-review-${r.id}`}><Trash2 size={18} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Promos({ promos, reload }) {
  const [form, setForm] = useState({ code: "", percent: "" });
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/promos", { code: form.code, percent: parseInt(form.percent, 10) });
      toast.success("Code promo créé"); setForm({ code: "", percent: "" }); reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/promos/${id}`); toast.success("Code supprimé"); reload(); };
  return (
    <div data-testid="promos-panel">
      <h1 className="font-display font-black text-4xl mb-8">Codes promo</h1>
      <form onSubmit={add} className="flex flex-col sm:flex-row gap-3 mb-10 p-6" style={{ background: "var(--white)", border: "1px solid var(--border)" }} data-testid="promo-form">
        <input className="field flex-1" placeholder="Code (ex: BIENVENUE10) *" required value={form.code}
          onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="promo-code-input" />
        <input className="field sm:w-40" type="number" min="1" max="90" placeholder="Remise % *" required value={form.percent}
          onChange={(e) => setForm({ ...form, percent: e.target.value })} data-testid="promo-percent-input" />
        <button className="btn-primary py-3 px-6 font-semibold flex items-center justify-center gap-2" data-testid="promo-submit"><Plus size={18} /> Créer</button>
      </form>
      <div className="space-y-px" style={{ background: "var(--border)", border: "1px solid var(--border)" }}>
        {promos.length === 0 && <p className="p-6" style={{ background: "var(--white)", color: "var(--ink-2)" }}>Aucun code promo.</p>}
        {promos.map((p) => (
          <div key={p.id} className="flex items-center gap-4 p-4" style={{ background: "var(--white)" }} data-testid={`admin-promo-${p.id}`}>
            <Tag size={18} style={{ color: "var(--orange)" }} />
            <div className="flex-1 min-w-0">
              <p className="font-semibold font-mono2">{p.code}</p>
              <p className="text-sm" style={{ color: "var(--ink-2)" }}>Remise −{p.percent}% · créé le {(p.created_at || "").slice(0, 10)}</p>
            </div>
            <button onClick={() => del(p.id)} className="p-2" style={{ color: "var(--orange-hover)" }} data-testid={`del-promo-${p.id}`}><Trash2 size={18} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Articles({ articles, reload }) {
  const empty = { title: "", category: "Conseils", excerpt: "", content: "", image: "" };
  const [form, setForm] = useState(empty);
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/articles", form);
      toast.success("Article publié"); setForm(empty); reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/articles/${id}`); toast.success("Article supprimé"); reload(); };
  return (
    <div data-testid="articles-panel">
      <h1 className="font-display font-black text-4xl mb-8">Conseils (blog)</h1>
      <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-10 p-6" style={{ background: "var(--white)", border: "1px solid var(--border)" }} data-testid="article-form">
        <input className="field" placeholder="Titre *" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="af-title" />
        <input className="field" placeholder="Catégorie" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="af-category" />
        <input className="field md:col-span-2" placeholder="Résumé (extrait) *" required value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} data-testid="af-excerpt" />
        <ImageField value={form.image} onChange={(v) => setForm({ ...form, image: v })} testid="af-image" />
        <textarea className="field md:col-span-2" rows={6} placeholder="Contenu (séparez les paragraphes par une ligne vide) *" required value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="af-content" />
        <button className="btn-primary py-3 px-6 font-semibold md:col-span-2 flex items-center justify-center gap-2" data-testid="af-submit"><Plus size={18} /> Publier l'article</button>
      </form>
      <div className="space-y-px" style={{ background: "var(--border)", border: "1px solid var(--border)" }}>
        {articles.map((a) => (
          <div key={a.id} className="flex items-center gap-4 p-4" style={{ background: "var(--white)" }} data-testid={`admin-article-${a.id}`}>
            <img src={a.image} alt="" className="w-14 h-14 object-cover" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{a.title}</p>
              <p className="text-sm" style={{ color: "var(--ink-2)" }}>{a.category} · /{a.slug}</p>
            </div>
            <button onClick={() => del(a.id)} className="p-2" style={{ color: "var(--orange-hover)" }} data-testid={`del-article-${a.id}`}><Trash2 size={18} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
